import React, { useState } from 'react';
import FileUploadButton from '../../../components/common/fileUpload';
import {
  isOrderReportFormat,
  isWarehouseTargetFormat,
  mapOrderReportToTargetFormat,
} from '../../../utils/wms/orderReportMapping';
import {
  convertExcelDataToJSONRows,
  convertExcelDateToComputerDate,
} from '../../../utils/excel';
import {
  getPendingWMSReport,
  saveSalesOrderForWH,
} from '../../../parse-functions/wms/report';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateToastInfo } from '../../../actions/settings';
import { updateSpinnerState } from '../../../actions/spinner';
import { useSelector } from 'react-redux';
import { returnWarehouseDropdownOptions } from '../../../utils/warehouse';
import DatePicker from '../../../components/common/DatePicker';
import { Button, Form } from '@themesberg/react-bootstrap';
import EditableDropDown from '../../../components/common/EditableDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders } from '@fortawesome/free-solid-svg-icons';
import TableOnly from '../../scheme-management/Components/IndividualScheme/TableOnly';
import { pendingInfoTable, pendingWMSReportColumns } from './utils';
import SchemeInfoModal from '../../scheme-management/Components/SchemRedesign/SchemeInfoModal';

const PendingWMSReport = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isAdmin = useSelector(
    (state) => state?.user?.preferences?.isAdmin || false
  );
  const otherWarehouses = useSelector(
    (state) => state?.user?.otherWarehousesInZone || {}
  );
  const warehouse = useSelector((state) => state?.user?.warehouse || {});
  const [formInput, setFormInput] = useState({
    warehouse: [],
    startDate: null,
    endDate: null,
  });
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoData, setInfoData] = useState([]);
  const [pendingReport, setPendingReport] = useState([]);

  const fileUploadComplete = async (fileData) => {
    try {
      let uploadedData = [];
      // Handle case where ExcelRenderer returns data in sheets format
      let actualRows = fileData.rows;
      if (fileData.sheets && fileData.sheets.length > 0) {
        actualRows = fileData.sheets[0].data;
      }

      // Check if it's Order Report-Detail format
      if (isOrderReportFormat({ rows: actualRows })) {
        uploadedData = mapOrderReportToTargetFormat(actualRows)?.filter(
          (row) => row['Voucher No']
        );
      }
      // Check if it's Warehouse Target format (existing format)
      else if (isWarehouseTargetFormat({ rows: actualRows })) {
        const firstSNoIndex = actualRows?.findIndex((arr) =>
          arr?.includes('SNo')
        );
        const allColumns = actualRows[firstSNoIndex];
        const allRows = actualRows.slice(firstSNoIndex + 1, actualRows.length);
        uploadedData = convertExcelDataToJSONRows(allColumns, allRows);

        // Convert date to excel readable date (existing logic)
        uploadedData.forEach((order) => {
          order['Delivery Date'] = convertExcelDateToComputerDate(
            order['Delivery Date'],
            true
          );
          order['Date'] = convertExcelDateToComputerDate(order['Date'], true);
        });
      }
      // Unknown format - try to process as Warehouse Target format (fallback)
      else {
        const firstSNoIndex = actualRows?.findIndex((arr) =>
          arr?.includes('SNo')
        );

        if (firstSNoIndex !== -1) {
          const allColumns = actualRows[firstSNoIndex];
          const allRows = actualRows.slice(
            firstSNoIndex + 1,
            actualRows.length
          );
          uploadedData = convertExcelDataToJSONRows(allColumns, allRows);

          uploadedData.forEach((order) => {
            order['Delivery Date'] = convertExcelDateToComputerDate(
              order['Delivery Date'],
              true
            );
            order['Date'] = convertExcelDateToComputerDate(order['Date'], true);
          });
        } else {
          throw new Error(
            'Unable to process file format. Please ensure the file is in the correct format.'
          );
        }
      }
      console.log(uploadedData);
      if (uploadedData && uploadedData.length > 0) {
        // Show success message with format detected
        dispatch(updateSpinnerState(true));
        const response = await saveSalesOrderForWH(uploadedData);
        dispatch(
          updateToastInfo({
            show: true,
            type: 'success',
            title: t('File Uploaded Successfully'),
            message: t(
              `File processed - New: ${response.savedOrders}, Exisiting: ${response.existingOrders}`
            ),
          })
        );
      } else {
        throw new Error('No valid data found in the uploaded file.');
      }
    } catch (error) {
      console.error('Error processing uploaded file:', error);
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('File Processing Error'),
          message: t(
            error.message ||
              'Failed to process uploaded file. Please check the format.'
          ),
        })
      );
    } finally {
      dispatch(updateSpinnerState(false));
    }
  };

  const onFilterSubmit = async () => {
    dispatch(updateSpinnerState(true));
    if (!formInput.startDate || !formInput.endDate) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('Fetching Orders failed'),
          message: t('Please select a valid date range'),
        })
      );
      dispatch(updateSpinnerState(false));
      return;
    }
    try {
      const filters = {
        warehouse: formInput.warehouse || [warehouse.warehouseCode],
        startDate: formInput.startDate,
        endDate: formInput.endDate,
      };
      const response = await getPendingWMSReport(filters);
      setPendingReport(response);
      dispatch(updateSpinnerState(false));
    } catch (e) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('Fetching Orders failed'),
          message: t(e.message),
        })
      );
      dispatch(updateSpinnerState(false));
    }
  };

  const onCellClick = (row) => {
    setInfoData(row);
    setShowInfoModal(true);
  };
  return (
    <div>
      <div className="d-flex p-3 justify-content-between align-items-center">
        <h3>Pending Report</h3>
        <FileUploadButton
          buttonText={'Upload File (.xlsx/.csv)'}
          onFileUpload={fileUploadComplete}
          style={{
            backgroundColor: '#007bff',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        />
      </div>
      <div className="table-settings mb-4 p-3">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '20px',
            width: '100%',
            backgroundColor: 'white',
          }}
        >
          <Form.Group>
            <Form.Label>From Date</Form.Label>
            <DatePicker
              value={formInput?.startDate}
              backgroundColor="white"
              setValue={(date) => {
                setFormInput((prev) => {
                  return {
                    ...prev,
                    startDate: date,
                  };
                });
              }}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>To Date</Form.Label>
            <DatePicker
              value={formInput?.endDate}
              backgroundColor="white"
              setValue={(date) => {
                setFormInput((prev) => {
                  return {
                    ...prev,
                    endDate: date,
                  };
                });
              }}
            />
          </Form.Group>
          {isAdmin && (
            <Form.Group className="mb-2">
              <Form.Label>{t('Select Warehouse')}</Form.Label>
              <EditableDropDown
                id={'warehouse'}
                onChange={(e) =>
                  setFormInput((prev) => ({
                    ...prev,
                    warehouse: e.map((item) => item.value),
                  }))
                }
                value={formInput['warehouse']}
                options={returnWarehouseDropdownOptions(otherWarehouses)}
                placeholder={'Choose Warehouse'}
                backgroundColor={'#F0F0F0'}
                isMultiSelect={true}
              ></EditableDropDown>
            </Form.Group>
          )}
          <div
            style={{
              gridColumn: 'span 3',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
            }}
          >
            <Button
              variant="white"
              className="fa-export"
              onClick={onFilterSubmit}
              style={{
                width: '150px',
                height: '38px',
                borderRadius: 0,
                border: '1px solid #262B40',
              }}
            >
              <FontAwesomeIcon icon={faSliders} />
              &nbsp;&nbsp;{t('Apply Filter')}
            </Button>
          </div>
        </div>
      </div>
      <TableOnly
        data={pendingReport?.branchCategoryStats}
        columns={pendingWMSReportColumns(
          onCellClick,
          pendingReport.allCategories
        )}
      />
      <SchemeInfoModal
        showModal={showInfoModal}
        closeModal={() => setShowInfoModal(false)}
        data={infoData}
        columns={pendingInfoTable()}
        title={t('Pending Parcels')}
      />
    </div>
  );
};

export default PendingWMSReport;
